import React, { useState, useRef, useContext } from "react";
import { Box, Container, Typography, Button, Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ReCAPTCHA from "react-google-recaptcha";
import { AuthContext } from "../user/BCPAuthProvider";
import { postData } from "./UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";

export default function CaptchaVerification({
  onVerificationSuccess,
  onGoBack
}) {
  const theme = useTheme();
  const authContext = useContext(AuthContext);
  const recaptchaRef = useRef(null);

  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [captchaValue, setCaptchaValue] = useState(null);

  const siteKey = getEnvironmentVariable("GOOGLE_RECAPTCHA_KEY");

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    setError(null);
  };

  const handleCaptchaExpired = () => {
    setCaptchaValue(null);
    setError("reCAPTCHA expired. Please try again.");
  };

  const handleCaptchaError = () => {
    setCaptchaValue(null);
    setError("reCAPTCHA error. Please try again.");
  };

  const handleVerification = async () => {
    if (!captchaValue) {
      setError("Please complete the reCAPTCHA verification.");
      return;
    }

    if (!authContext?.authState?.identity?.userId) {
      setError("User not authenticated. Please log in and try again.");
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      await postData(
        `/users/${authContext.authState.identity.userId}/verifycaptcha`, {
          body: {
            "g-recaptcha-response": captchaValue
          }}
      );

      onVerificationSuccess();
    } catch (err) {
      console.error("Captcha verification failed:", err);
      setError("Verification failed. Please try again.");

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaValue(null);
    } finally {
      setIsVerifying(false);
    }
  };

  if (!siteKey) {
    return (
      <Box
        sx={{
          background:
            theme?.palette?.mode === "dark"
              ? "rgba(0,0,0,0.7)"
              : "rgba(255,255,255,0.7)",
          minHeight: "calc(100vh - 64px)",
          mb: -2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <Container maxWidth="md">
          <Alert severity="error">
            reCAPTCHA configuration error. Please contact support.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background:
          theme?.palette?.mode === "dark"
            ? "rgba(0,0,0,0.7)"
            : "rgba(255,255,255,0.7)",
        minHeight: "calc(100vh - 64px)",
        mb: -2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            textAlign: "center",
            backgroundColor: theme?.palette?.background?.paper,
            padding: 4,
            borderRadius: 2,
            boxShadow: theme?.shadows?.[3]
          }}
        >
          <Typography variant="h5" gutterBottom>
            Security Verification Required
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
            To continue viewing army lists, please complete the security
            verification below.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3
            }}
          >
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={siteKey}
              onChange={handleCaptchaChange}
              onExpired={handleCaptchaExpired}
              onError={handleCaptchaError}
              theme={theme?.palette?.mode === "dark" ? "dark" : "light"}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="outlined"
              onClick={onGoBack}
              disabled={isVerifying}
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              onClick={handleVerification}
              disabled={!captchaValue || isVerifying}
            >
              {isVerifying ? "Verifying..." : "Verify"}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
